/**
 * Account enrichment — governed firmographic research (Ask #1).
 *
 * Pure helpers behind the per-account "Enrich" action. The flow is:
 *
 *   1. `buildEnrichmentPrompt(account)` — turn the account's known,
 *      operator-entered facts (name, POC-derived domain, workflow names)
 *      into a strict-JSON research prompt for the real Claude behind the
 *      hosted agent-worker (`runAgent` in argoWorker.ts).
 *   2. `parseEnrichmentReply(text)` — defensively parse the model's free
 *      text into `EnrichmentFields`. Garbage in → `{}` out, never a throw.
 *   3. `enrichmentToActions(account, fields)` — map each returned field to
 *      an `update_brain` action targeting the account's Company Brain, to
 *      be queued through the approvals inbox (`queueApproval`) so the
 *      operator explicitly approves every write. Never a silent write.
 *
 * NON-NEGOTIABLE: nothing here fabricates data. Every suggested value
 * originates from the model's reply; these helpers only transport and
 * validate. When the worker isn't configured the UI shows an honest
 * "connect AI to enrich" message instead of simulating research.
 */

import type { Account } from '../db/types';
import type { UpdateBrainAction } from './types';

/** Firmographic fields the model may propose. All optional — unknowns are omitted. */
export interface EnrichmentFields {
  /** e.g. "Fintech — payments infrastructure". */
  industry?: string;
  /** e.g. "200-500 employees". */
  size?: string;
  /** One-paragraph company summary. */
  summary?: string;
  /** Likely buyer/stakeholder roles, e.g. ["CTO", "VP Operations"]. */
  stakeholderRoles?: string[];
}

/** Where each suggested field lands in the account's Company Brain. */
const FIELD_TARGETS: {
  key: keyof EnrichmentFields;
  section: UpdateBrainAction['section'];
  field: string;
  label: string;
}[] = [
  { key: 'industry', section: 'identity', field: 'industry', label: 'Industry' },
  { key: 'size', section: 'identity', field: 'size', label: 'Company size' },
  { key: 'summary', section: 'identity', field: 'summary', label: 'Company summary' },
  {
    key: 'stakeholderRoles',
    section: 'relationships',
    field: 'stakeholderRoles',
    label: 'Likely stakeholder roles',
  },
];

/**
 * Derive the company domain from the primary POC's email. Returns null when
 * there's nothing email-shaped to derive from — we never guess a domain.
 */
export function accountDomain(account: Account): string | null {
  const email = account.pocEmail?.trim() ?? '';
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  return domain.includes('.') ? domain : null;
}

/**
 * Build the research prompt. Only facts we actually have go in; the model
 * is told to return strict JSON and to omit anything it can't support —
 * the "do not fabricate" rule is enforced both here and in the parser.
 */
export function buildEnrichmentPrompt(account: Account): string {
  const domain = accountDomain(account);
  const workflows = account.workflows
    .map((w) => w.name)
    .filter(Boolean)
    .join(', ');

  const known = [
    `Company: ${account.name}`,
    domain ? `Domain: ${domain}` : null,
    workflows ? `Active engagements with us: ${workflows}` : null,
  ].filter(Boolean);

  return [
    'You are a B2B account-research assistant. Research the company below',
    'and propose firmographic enrichment for our CRM.',
    '',
    ...known,
    '',
    'Reply with ONLY a single JSON object — no prose, no code fences — of this shape:',
    '{',
    '  "industry": string,          // primary industry / category',
    '  "size": string,              // headcount band, e.g. "200-500 employees"',
    '  "summary": string,           // one-paragraph company summary',
    '  "stakeholderRoles": string[] // likely buyer/stakeholder job titles',
    '}',
    '',
    'Rules: do not guess or fabricate. Omit any key you cannot support with',
    'real knowledge of this company. If you know nothing reliable, reply {}.',
  ].join('\n');
}

const cleanString = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t : undefined;
};

/**
 * Parse the model's reply into structured fields. Tolerates prose and code
 * fences around the JSON; drops wrong-typed or empty values; NEVER throws.
 */
export function parseEnrichmentReply(text: string): EnrichmentFields {
  const raw = extractJsonObject(text);
  if (!raw) return {};

  const out: EnrichmentFields = {};
  const industry = cleanString(raw.industry);
  const size = cleanString(raw.size);
  const summary = cleanString(raw.summary);
  if (industry) out.industry = industry;
  if (size) out.size = size;
  if (summary) out.summary = summary;

  if (Array.isArray(raw.stakeholderRoles)) {
    const roles = raw.stakeholderRoles
      .map(cleanString)
      .filter((r): r is string => Boolean(r));
    if (roles.length > 0) out.stakeholderRoles = roles;
  }
  return out;
}

/** Best-effort extraction of the first {...} object from free text. */
function extractJsonObject(text: string): Record<string, unknown> | null {
  const tryParse = (s: string): Record<string, unknown> | null => {
    try {
      const parsed: unknown = JSON.parse(s);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  };
  const trimmed = text.trim();
  if (!trimmed) return null;
  const direct = tryParse(trimmed);
  if (direct) return direct;
  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  return tryParse(trimmed.slice(start, end + 1));
}

/**
 * Map parsed fields onto governed `update_brain` proposals. Confidence is
 * modest (model-inferred, unverified by an operator yet) and each action
 * carries an evidence pointer naming the enrichment run — the approvals
 * inbox shows exactly what will be written before anything commits.
 */
export function enrichmentToActions(
  account: Account,
  fields: EnrichmentFields,
): UpdateBrainAction[] {
  const actions: UpdateBrainAction[] = [];
  for (const target of FIELD_TARGETS) {
    const v = fields[target.key];
    const value = Array.isArray(v) ? v.join(', ') : v;
    if (!value) continue;
    actions.push({
      kind: 'update_brain',
      accountId: account.id,
      accountName: account.name,
      section: target.section,
      field: target.field,
      label: target.label,
      value,
      confidence: 0.6,
      evidence: `AI enrichment (Claude via agent-worker) for ${account.name}`,
    });
  }
  return actions;
}
